import { useFilterDisabledSettingsV2 } from "@api";
import type { ICer } from "@bbj-types/candidate/cer";
import { ExternalIntegrationStatusLabel } from "@data-display/ExternalIntegrationStatusLabel";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useI18nContext } from "@root/utils/organisation";
import { PaperCollapse } from "@surfaces/PaperCollapse";
import { useMemo, useState } from "react";
import { chipProps } from "../CerCard/constant";
import { isCerDone } from "../CerCard/logics";
import { CerExternalStack, classes } from "./styles";

export interface ICerExternalProps {
  cer: Pick<
    ICer,
    "dates" | "structure" | "type" | "challenges" | "beneficiaryProject" | "status" | "externalIntegrations"
  >;
}
export const CerExternal = ({ cer }: ICerExternalProps) => {
  const { settingsV2 } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const cerTypes = useFilterDisabledSettingsV2(settingsV2.cerTypes, cer.type);

  const type = useMemo(() => cerTypes.find((t) => t.value === cer.type)?.label ?? "", [cer]);

  const cerStartDate = cer.dates.startDate.toLocaleString("fr-FR", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone,
  });

  const cerEndDate = cer.dates.endDate.toLocaleString("fr-FR", {
    day: "numeric",
    month: "numeric",
    year: "numeric",
    timeZone,
  });

  const [open, setOpen] = useState(false);

  return (
    <PaperCollapse
      open={Boolean(open)}
      fullWidth
      onOpen={() => setOpen((open) => !open)}
      actionButtons={
        open ? (
          <IconButton aria-label="expand row" color="primary" size="small">
            <KeyboardArrowUp />
          </IconButton>
        ) : (
          <IconButton aria-label="collapse row" color="primary" size="small">
            <KeyboardArrowDown />
          </IconButton>
        )
      }
      title={
        <CerExternalStack direction="row" className={classes.root} gap={3}>
          <Stack>
            <Typography variant="body1">{`CE du ${cerStartDate} au ${cerEndDate}`}</Typography>
            {cer.externalIntegrations?.map((integration) => (
              <ExternalIntegrationStatusLabel
                key={integration.provider}
                provider={integration.provider}
                status={integration.status}
                error={integration.error}
                variant="body2"
                sx={{ lineHeight: 0.5 }}
              />
            ))}
          </Stack>
          {isCerDone(cer) ? (
            <Chip label="Terminé" variant="outlined" className={classes.doneChip} size="small" />
          ) : (
            <>
              <Chip {...chipProps[cer.status]} size="small" />
            </>
          )}
        </CerExternalStack>
      }
    >
      <Stack
        direction="column"
        sx={{
          gap: 1,
        }}
      >
        <Box>
          {cer.structure.author && (
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Typography variant="body1SemiBold">Référent:</Typography>
              <Typography>
                {cer.structure.author.lastName} {cer.structure.author.firstName}
              </Typography>
            </Stack>
          )}
          {cer.structure.team && (
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Typography variant="body1SemiBold">Structure Référente:</Typography>
              <Typography>{cer.structure.team.name}</Typography>
            </Stack>
          )}
          {type && (
            <Stack
              direction="row"
              sx={{
                gap: 0.5,
              }}
            >
              <Typography variant="body1SemiBold">Type de CER:</Typography>
              <Typography>{type}</Typography>
            </Stack>
          )}
          <br />
          {cer.beneficiaryProject.comment && (
            <Stack direction="column">
              <Typography variant="body1SemiBold">Commentaire CER:</Typography>
              <Typography>{cer.beneficiaryProject.comment}</Typography>
            </Stack>
          )}
          <br />
          {cer.challenges && (
            <Stack direction="column">
              <Typography variant="body1SemiBold">Objectifs:</Typography>
              <Stack
                direction="column"
                sx={{
                  gap: 0.5,
                }}
              >
                {cer.challenges.map((challenge) => (
                  <Box key={challenge._id}>
                    {challenge.objectives.map((objective) => (
                      <Typography key={objective._id} sx={{ ml: 1 }}>
                        • {objective.objective?.name}
                        {objective.label ? ` - ${objective.label}` : ""}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
    </PaperCollapse>
  );
};
